import { useState } from 'react';

import { ErrorBox, Spinner, formatPhone, useAsync, won } from '@solar/ui';

import { api } from '../api';

function today(): string {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

/**
 * 일일 수납 — iOS DayPayView 파리티.
 *
 * 레거시 DayPayView 는 WKWebView 로 `/web/dashboard`(기사 카드 4종:
 * 내게 할당 주문/미할당 주문/내게 할당 A\/S/미할당 A\/S)를 띄우는 화면이었다
 * (DashboardController.php:137-142). 신규 `GET /dashboard` 가 같은 카드를
 * 내려주므로 그대로 쓰고, "일일 수납" 이라는 이름값은 일자별 본인 배정 건의
 * 수납 요약으로 재현한다 — 별도 서버 API 없이 `GET /orders` 필터만 쓴다.
 */
export function DayPay({ onOpenOrder }: { onOpenOrder: (id: number) => void }) {
  const dash = useAsync(() => api.dashboard(), []);

  const [date, setDate] = useState(today());
  const orders = useAsync(
    () =>
      api.listOrders({
        status: 'all',
        assigned_to_me: true,
        install_date_from: date,
        install_date_to: date,
        size: 200,
      }),
    [date],
  );

  const items = orders.data?.items ?? [];
  const paid = items.filter((o) => o.payment_check === 'Y');
  const completed = items.filter((o) => o.install_complete === 'Y');
  const paidSum = paid.reduce((sum, o) => sum + (o.payment ?? 0), 0);
  const unpaidSum = items
    .filter((o) => o.payment_check !== 'Y')
    .reduce((sum, o) => sum + (o.payment ?? 0), 0);

  return (
    <>
      {dash.error && <ErrorBox message={dash.error} onRetry={dash.reload} />}
      {dash.data && (
        <div className="stats">
          {dash.data.items.map((it) => (
            <div key={it.key} className="stat">
              <strong>{it.count}</strong>
              <span>{it.label}</span>
            </div>
          ))}
        </div>
      )}

      <h2 className="section">일자별 수납 현황</h2>
      <label className="input">
        설치일
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
      </label>

      {orders.loading && <Spinner />}
      {orders.error && <ErrorBox message={orders.error} onRetry={orders.reload} />}

      {orders.data && (
        <>
          <div className="stats">
            <div className="stat">
              <strong>{items.length}</strong>
              <span>배정 건</span>
            </div>
            <div className="stat">
              <strong>{completed.length}</strong>
              <span>설치완료</span>
            </div>
            <div className="stat">
              <strong>{paid.length}</strong>
              <span>수납완료</span>
            </div>
            <div className="stat">
              <strong>{won(paidSum)}</strong>
              <span>수납 합계</span>
            </div>
          </div>

          {unpaidSum > 0 && (
            <p className="warn">미수납 자부담금 합계: {won(unpaidSum)}</p>
          )}

          {items.length === 0 && <p className="empty">해당 일자에 배정된 건이 없습니다.</p>}

          <ul className="cards">
            {items.map((o) => (
              <li key={o.id}>
                <button type="button" className="card" onClick={() => onOpenOrder(o.id)}>
                  <div className="card__top">
                    <strong>{o.user_name ?? '(이름 없음)'}</strong>
                    {o.payment_check === 'Y' ? (
                      <span className="badge paid">수납 {won(o.payment)}</span>
                    ) : (
                      <span className="badge unpaid">미수납 {won(o.payment)}</span>
                    )}
                  </div>
                  <p className="card__addr">{o.addr_full ?? '-'}</p>
                  <p className="card__meta">
                    {formatPhone(o.user_phone)}
                    {o.install_complete === 'Y' ? ' · 설치완료' : ' · 미설치'}
                  </p>
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </>
  );
}
